/**
 * Where the assistant's round launcher may float (tablet pass 2026-10-20, item 1). The rule: it
 * never covers an interactive element.
 *
 *   hidden  on a registry module's routes (their split is bounded to the viewport, so their
 *           controls reach the bottom-right corner and nothing can scroll clear of it) and below
 *           RAIL_DEFAULT_BELOW, 1,202 px of shell (the tablet: the amend editor's Submit at 1024,
 *           the bottom nav's More at 768). The assistant stays reachable: the header's Assistant
 *           button (AssistantHeaderButton), at every width (round 2, P2: the mobile layout's
 *           opener was inside More, so none showed). Exactly one opener at a time.
 *   shown   elsewhere, and MainLayout gives <main> bottom padding equal to its footprint, so the
 *           last control on a page can always scroll clear of it.
 *
 * openAssistant() opens the panel from anywhere (the header's button) through a window event, so
 * nothing has to hold the widget's state.
 */
import { useLocation } from 'react-router-dom';
import { moduleForPath } from '../../modules/registry';
import { RAIL_DEFAULT_BELOW } from '../../utils/sidebarLayout';
import useViewportWidth from '../layout/tools/useViewportWidth';

export const ASSISTANT_OPEN_EVENT = 'assistant:open';

/** The launcher: 56 px, 24 px from the bottom and right edges. */
export const ASSISTANT_OFFSET = 24;
export const ASSISTANT_SIZE = 56;
/** What <main> reserves at its foot while the launcher shows. */
export const ASSISTANT_FOOTPRINT = ASSISTANT_OFFSET + ASSISTANT_SIZE;

/** The mobile layout (MainLayout's breakpoint) and its bottom nav, which the panel sits above. */
export const MOBILE_BELOW = 1024;
export const MOBILE_NAV_HEIGHT = 72;

/** Whether the launcher is hidden for this path at this viewport width. */
export function launcherHiddenFor(pathname, viewportWidth) {
  return !!moduleForPath(pathname) || !(viewportWidth >= RAIL_DEFAULT_BELOW);
}

/** The rule for the current route and window, following resizes. */
export function useAssistantLauncherHidden() {
  const { pathname } = useLocation();
  const viewport = useViewportWidth();
  return launcherHiddenFor(pathname, viewport);
}

/** Open the assistant's panel (the header's button). */
export function openAssistant() {
  window.dispatchEvent(new Event(ASSISTANT_OPEN_EVENT));
}
